import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  AdminButton,
  AdminCard,
  AdminCardHeader,
  AdminInfoRow,
  AdminMetricCard,
  AdminPageLayout,
  AdminPagination,
  AdminSearchInput,
  AdminSelect,
  AdminStatusBadge,
  AdminTable,
  AdminTd,
  AdminTextarea,
  AdminTr,
} from "@/components/admin";
import { fetchAdminDailySchedules, updateAdminDailyScheduleActive } from "@/lib/api";
import type { AdminDailyScheduleItem } from "@/types";
import { formatDateTime } from "@/lib/format";

type ActiveFilter = "all" | "active" | "inactive";

const scheduleTypeFilters = [
  { key: "", label: "전체" },
  { key: "GENERAL", label: "일반" },
  { key: "MEDICATION", label: "복약" },
  { key: "MEAL", label: "식사" },
  { key: "VISIT", label: "방문" },
  { key: "HEALTH_CHECK", label: "건강" },
  { key: "ETC", label: "기타" },
];

const pageSize = 10;

const getActiveParam = (filter: ActiveFilter) => {
  if (filter === "active") return true;
  if (filter === "inactive") return false;
  return null;
};

const DailySchedulesPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const seniorFilterId = Number(searchParams.get("seniorUserAccountId"));
  const queryScheduleId = Number(searchParams.get("scheduleId"));
  const [items, setItems] = useState<AdminDailyScheduleItem[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [keyword, setKeyword] = useState("");
  const [scheduleType, setScheduleType] = useState("");
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [reasonCode, setReasonCode] = useState("OPERATOR_REQUEST");
  const [reason, setReason] = useState("");
  const [auditMessage, setAuditMessage] = useState<string | null>(null);

  const loadSchedules = useCallback(async () => {
    setLoading(true);
    setErrorMessage(null);
    try {
      const nextItems = await fetchAdminDailySchedules({
        keyword,
        scheduleType,
        active: getActiveParam(activeFilter),
      });
      const filteredBySenior = Number.isFinite(seniorFilterId) && seniorFilterId > 0
        ? nextItems.filter((item) => item.seniorUserAccountId === seniorFilterId)
        : nextItems;
      setItems(filteredBySenior);
      setSelectedId((current) =>
        current != null && filteredBySenior.some((item) => item.elderlyDailyScheduleId === current)
          ? current
          : filteredBySenior[0]?.elderlyDailyScheduleId ?? null,
      );
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "생활 일정을 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }, [activeFilter, keyword, scheduleType, seniorFilterId]);

  useEffect(() => {
    void loadSchedules();
  }, [loadSchedules]);
  useEffect(() => {
    if (Number.isFinite(queryScheduleId) && queryScheduleId > 0) {
      setSelectedId(queryScheduleId);
    }
  }, [queryScheduleId]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeFilter, keyword, scheduleType]);

  const summary = useMemo(() => {
    const activeCount = items.filter((item) => item.active).length;
    const inactiveCount = items.length - activeCount;
    const noResponseCount = items.filter((item) => item.noResponseRealert).length;
    const recurringCount = items.filter((item) => item.repeatType === "DAILY" || item.repeatType === "WEEKLY").length;
    return { total: items.length, activeCount, inactiveCount, noResponseCount, recurringCount };
  }, [items]);

  const totalPages = Math.ceil(items.length / pageSize);
  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return items.slice(start, start + pageSize);
  }, [currentPage, items]);

  const selectedItem = useMemo(
    () => items.find((item) => item.elderlyDailyScheduleId === selectedId) ?? null,
    [items, selectedId],
  );

  const handleToggleActive = useCallback(
    async (item: AdminDailyScheduleItem) => {
      setSavingId(item.elderlyDailyScheduleId);
      setErrorMessage(null);
      try {
        const result = await updateAdminDailyScheduleActive(item.elderlyDailyScheduleId, !item.active, reasonCode, reason);
        const updated = result.schedule;
        setItems((current) =>
          current.map((schedule) =>
            schedule.elderlyDailyScheduleId === updated.elderlyDailyScheduleId ? updated : schedule,
          ),
        );
        setAuditMessage(`감사 #${result.audit.auditLogId} · ${result.audit.beforeStatus} → ${result.audit.afterStatus}`);
        setReason("");
      } catch (error) {
        setErrorMessage(error instanceof Error ? error.message : "생활 일정 상태 변경에 실패했습니다.");
      } finally {
        setSavingId(null);
      }
    },
    [reason, reasonCode],
  );

  const quickFilters: { key: ActiveFilter; label: string; count: number }[] = [
    { key: "all", label: "전체", count: summary.total },
    { key: "active", label: "활성", count: summary.activeCount },
    { key: "inactive", label: "비활성", count: summary.inactiveCount },
  ];

  return (
    <AdminPageLayout
      title="생활 일정"
      subtitle={Number.isFinite(seniorFilterId) && seniorFilterId > 0 ? `Senior ID ${seniorFilterId} 생활 일정 조회 및 운영 상태 관리` : "Senior 생활 일정 조회 및 운영 상태 관리"}
      actions={
        <AdminButton size="sm" variant="soft" icon={loading ? "ri-loader-4-line animate-spin" : "ri-refresh-line"} onClick={() => void loadSchedules()} disabled={loading}>
          새로고침
        </AdminButton>
      }
      contentTop="compact"
    >
      {errorMessage && <div className="mb-4 rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-xs text-red-700">{errorMessage}</div>}

      <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 mb-4">
        <AdminMetricCard title="전체 일정" value={`${summary.total}건`} description="조회 조건 기준" icon="ri-calendar-schedule-line" tone="blue" />
        <AdminMetricCard title="활성 일정" value={`${summary.activeCount}건`} description="시니어앱 실행 대상" icon="ri-play-circle-line" tone="emerald" />
        <AdminMetricCard title="반복 일정" value={`${summary.recurringCount}건`} description="매일 또는 매주 반복" icon="ri-repeat-line" tone="amber" />
        <AdminMetricCard title="미응답 재알림" value={`${summary.noResponseCount}건`} description="Keeper 알림 후보" icon="ri-notification-3-line" tone="orange" />
      </section>

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-4">
        <div className="xl:col-span-3">
          <AdminCard>
            <AdminCardHeader
              title="생활 일정 목록"
              icon="ri-list-check-3"
              action={<span className="text-xs text-slate-400">총 {items.length}건</span>}
            />
            <div className="px-4 py-3 border-b border-slate-100 flex flex-col lg:flex-row gap-2 lg:items-center lg:justify-between">
              <AdminSearchInput value={keyword} onChange={setKeyword} placeholder="Senior, Keeper, 일정명 검색" className="min-w-0 flex-1" />
              <div className="flex flex-wrap gap-1.5">
                {scheduleTypeFilters.map((filter) => (
                  <AdminButton variant="plain" size="auto"
                    key={filter.key || "all"}
                    type="button"
                    onClick={() => setScheduleType(filter.key)}
                    className={`h-8 px-2.5 rounded-lg border text-xs font-medium transition-colors ${
                      scheduleType === filter.key ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"
                    }`}
                  >
                    {filter.label}
                  </AdminButton>
                ))}
              </div>
            </div>

            <AdminTable
              headers={["어르신", "일정", "유형", "시간", "반복", "옵션", "상태"]}
              loading={loading}
              emptyMessage={!loading && paginatedItems.length === 0 ? "표시할 생활 일정이 없습니다." : undefined}
            >
              {paginatedItems.map((item) => {
                const selected = selectedId === item.elderlyDailyScheduleId;
                return (
                  <AdminTr
                    key={item.elderlyDailyScheduleId}
                    onClick={() => setSelectedId(item.elderlyDailyScheduleId)}
                    className={`cursor-pointer text-xs ${selected ? "bg-blue-50/70" : "hover:bg-slate-50"}`}
                  >
                    <AdminTd>
                      <div className="font-semibold text-slate-700">{item.elderlyName}</div>
                      <div className="text-xs text-slate-400">ID {item.seniorUserAccountId}</div>
                    </AdminTd>
                    <AdminTd>
                      <div className="font-medium text-slate-700">{item.title}</div>
                    </AdminTd>
                    <AdminTd className="whitespace-nowrap">
                      <AdminStatusBadge label={item.scheduleTypeName} code={item.scheduleType} tone="blue" />
                    </AdminTd>
                    <AdminTd className="text-slate-700 font-semibold whitespace-nowrap">{formatDateTime(item.scheduleTime, 0, 5)}</AdminTd>
                    <AdminTd className="text-slate-600 whitespace-nowrap">{item.repeatLabel}</AdminTd>
                    <AdminTd>
                      <div className="flex flex-wrap gap-1">
                        {item.noResponseRealert && <span className="px-1.5 py-0.5 rounded bg-orange-50 text-orange-700 text-xs">재알림</span>}
                        {item.completeCheck && <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 text-xs">완료확인</span>}
                        {item.retryCount > 0 && <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 text-xs">재시도 {item.retryCount}회</span>}
                      </div>
                    </AdminTd>
                    <AdminTd className="whitespace-nowrap">
                      <AdminStatusBadge
                        label={item.active ? "활성" : "비활성"}
                        code={item.active ? "ACTIVE" : "INACTIVE"}
                        tone={item.active ? "emerald" : "slate"}
                        className="h-7 min-w-16 justify-center text-xs font-semibold"
                      />
                    </AdminTd>
                  </AdminTr>
                );
              })}
            </AdminTable>
            <AdminPagination currentPage={currentPage} totalPages={totalPages} totalCount={items.length} pageSize={pageSize} onPageChange={setCurrentPage} />
          </AdminCard>
        </div>

        <div className="xl:col-span-1 flex flex-col gap-4">
          <AdminCard>
            <AdminCardHeader title="일정 상세" icon="ri-calendar-check-line" />
            {selectedItem ? (
              <div className="p-4">
                <div className="mb-3">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="text-sm font-bold text-slate-800 truncate">{selectedItem.title}</h3>
                    <AdminStatusBadge label={selectedItem.active ? "활성" : "비활성"} tone={selectedItem.active ? "emerald" : "slate"} />
                  </div>
                  <p className="text-xs text-slate-400 mt-1">{selectedItem.elderlyName} · {selectedItem.scheduleTypeName}</p>
                </div>
                <div className="border-y border-slate-100 py-1">
                  <AdminInfoRow dense bordered label="Senior" value={`${selectedItem.elderlyName} (ID ${selectedItem.seniorUserAccountId})`} />
                  <AdminInfoRow dense bordered label="Keeper" value={selectedItem.guardianName} />
                  <AdminInfoRow dense bordered label="시간" value={formatDateTime(selectedItem.scheduleTime, 0, 5)} />
                  <AdminInfoRow dense bordered label="반복" value={selectedItem.repeatLabel} />
                  <AdminInfoRow dense bordered label="실행일" value={selectedItem.scheduleDate} />
                  <AdminInfoRow dense bordered label="미응답 재알림" value={selectedItem.noResponseRealert ? "사용" : "미사용"} />
                  <AdminInfoRow dense bordered label="완료 확인" value={selectedItem.completeCheck ? "사용" : "미사용"} />
                  <AdminInfoRow dense bordered label="재시도 정책" value={`${selectedItem.retryCount}회 · ${selectedItem.retryIntervalMin}분 간격`} />
                </div>
                <div className="mt-3 grid grid-cols-1 gap-2">
                  <AdminButton
                    type="button"
                    variant="secondary"
                    size="sm"
                    icon="ri-user-heart-line"
                    onClick={() => navigate(`/admin/users/seniors/${selectedItem.seniorUserAccountId}`)}
                  >
                    Senior 통합 상세 보기
                  </AdminButton>
                  <AdminButton
                    type="button"
                    variant="soft"
                    size="sm"
                    icon="ri-heart-pulse-line"
                    onClick={() => navigate(`/admin/users/seniors/${selectedItem.seniorUserAccountId}?tab=care`)}
                  >
                    생활신호 보기
                  </AdminButton>
                </div>
                <div className="mt-4 border-t border-slate-100 pt-3 space-y-2">
                  <AdminSelect value={reasonCode} onValueChange={setReasonCode} disabled={savingId != null}>
                    <option value="OPERATOR_REQUEST">운영자 요청</option><option value="DATA_CORRECTION">데이터 정정</option><option value="SECURITY_RESPONSE">보안 대응</option><option value="TEST_ROLLBACK">테스트 원복</option><option value="OTHER">기타</option>
                  </AdminSelect>
                  <AdminTextarea value={reason} onChange={(event) => setReason(event.target.value)} maxLength={500} rows={3} placeholder="변경 사유를 10자 이상 입력하세요." disabled={savingId != null} />
                  <AdminButton type="button" size="sm" className="w-full" disabled={savingId != null || reason.trim().length < 10} onClick={() => void handleToggleActive(selectedItem)}>
                    {selectedItem.active ? "일정 비활성화" : "일정 활성화"}
                  </AdminButton>
                  {auditMessage && <p className="text-xs text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2">{auditMessage}</p>}
                </div>
                <div className="mt-3 text-xs text-slate-400">
                  수정: {formatDateTime(selectedItem.modDt)}
                </div>
              </div>
            ) : (
              <div className="px-4 py-10 text-center text-xs text-slate-400">일정을 선택하세요.</div>
            )}
          </AdminCard>

          <AdminCard padded>
            <div className="flex items-center gap-2 mb-3">
              <i className="ri-filter-3-line text-slate-500 text-sm" />
              <h3 className="text-sm font-semibold text-slate-800">빠른 필터</h3>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {quickFilters.map((filter) => (
                <AdminButton variant="plain" size="sm"
                  key={filter.key}
                  type="button"
                  onClick={() => setActiveFilter(filter.key)}
                  className={`rounded-md border ${
                    activeFilter === filter.key ? "bg-blue-50 text-blue-600 border-blue-200" : "text-slate-500 border-slate-200 hover:bg-slate-50"
                  }`}
                >
                  {filter.label}
                  <span className="ml-1 text-xs">{filter.count}</span>
                </AdminButton>
              ))}
            </div>
          </AdminCard>

          <AdminCard padded>
            <div className="flex items-center gap-2 mb-2">
              <i className="ri-information-line text-amber-600 text-sm" />
              <h3 className="text-sm font-semibold text-slate-700">운영 기준</h3>
            </div>
            <p className="text-xs text-slate-500 leading-relaxed">
              이 화면은 현행 관리자 일정 API를 조회하고 활성 상태를 변경합니다. 일정 응답은 예정·응답 완료·무응답의 세 상태로 운영합니다.
            </p>
          </AdminCard>
        </div>
      </div>
    </AdminPageLayout>
  );
};

export default DailySchedulesPage;
